import { useRef, useState } from "react";

export const MAX_ATTACHMENT_COUNT = 5;
export const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

interface AttachmentPickerProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentPicker({
  files,
  onChange,
  disabled = false,
}: AttachmentPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const incoming = Array.from(selectedFiles);
    const oversized = incoming.find((file) => file.size > MAX_ATTACHMENT_SIZE_BYTES);
    if (oversized) {
      setError(`“${oversized.name}” supera el límite de 10 MB.`);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    const uniqueFiles = [...files];
    for (const file of incoming) {
      const isDuplicate = uniqueFiles.some(
        (current) =>
          current.name === file.name &&
          current.size === file.size &&
          current.lastModified === file.lastModified
      );
      if (!isDuplicate) uniqueFiles.push(file);
    }

    if (uniqueFiles.length > MAX_ATTACHMENT_COUNT) {
      setError(`Puedes adjuntar hasta ${MAX_ATTACHMENT_COUNT} archivos por envío.`);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setError(null);
    onChange(uniqueFiles);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    setError(null);
    onChange(files.filter((_, fileIndex) => fileIndex !== index));
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple
        disabled={disabled}
        onChange={(event) => handleFiles(event.target.files)}
        className="sr-only"
      />

      <button
        type="button"
        disabled={disabled || files.length >= MAX_ATTACHMENT_COUNT}
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-50 px-3.5 py-2.5 text-xs font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <i className="ri-attachment-2" aria-hidden="true" />
        Adjuntar archivos
      </button>

      <span className="ml-2 text-[11px] text-foreground-400">
        Máximo {MAX_ATTACHMENT_COUNT} archivos de 10 MB
      </span>

      {error && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}

      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="flex items-center gap-2.5 rounded-lg border border-background-200 bg-background-100/70 px-3 py-2"
            >
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-500">
                <i
                  className={file.type.startsWith("image/") ? "ri-image-line" : "ri-file-line"}
                  aria-hidden="true"
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-medium text-foreground-800">
                  {file.name}
                </span>
                <span className="block text-[10px] text-foreground-400">
                  {formatFileSize(file.size)}
                </span>
              </span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                disabled={disabled}
                aria-label={`Quitar ${file.name}`}
                className="flex h-7 w-7 items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed"
              >
                <i className="ri-close-line" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
